import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  input,
  signal,
} from '@angular/core';
import { AbstractControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { FileUploadComponent } from '../../../shared-components/aems-file-upload/file-upload.component';
import { CreateWorkOrderRepairPartsComponent } from '../repair-parts/cwo-repair-parts.component';
import { RepairLineItemForm } from '../../../form-model/cwo-repair-line-item.form.model';
import { IMcTypeaheadData } from '@maersk-global/mds-components-core/mc-typeahead/types';
import { ContainerGrade } from '../../../models/container-grade.model';
import { Repair } from '../../../models/repair.model';
import { Part } from '../../../models/part.model';
import { utils } from '../../../helper/utils';
import { combineLatest, ReplaySubject, startWith, takeUntil } from 'rxjs';
import { typeaheadCodeExistValidator } from '../../../shared-validators/typeahead-code-exist.validator';
import { MasterDataService } from '../../../services/master-data.service';
import { Equipment } from '../../../models/equipment.model';
import { Mode } from '../../../models/mode.model';
import { containerGradeValidator } from './validators/container-grade.validator';
import { repairSuspendedValidator } from './validators/repair-suspended.validator';
import { Shop } from '../../../models/shop.model';
import { duplicateRepairCodeValidator } from './validators/duplicate-repair-code-validator';
import { WorkOrderService } from '../../../services/work-order.service';
import { ptiStatusValidator } from './validators/pti-status.validator';
import { CodeDescription } from '../../../models/code-description.model';
import { RepairLocation } from '../../../models/repair-location.model';
import { ThirdPartyIndicator, getTPIChar } from '../../../models/tpi.model';
import { MaintenanceOrderLineItem } from '../../../models/maintenance-order-line-item';
import { nonEmptyReeferRepairWarning } from './validators/nonempty-reefer-repair-warning.validator';
import { Currency } from '../../../models/currency.model';
import { ToastrService } from '../../../services/toastr-service';
import { SharedDataService } from '../../../services/shared-data.service';
import { MaintenanceOrderPart } from '../../../models/maintenance-order-part';
import { ApplicationtTextService } from '../../../services/application-text.service';
import { RegexConstants } from '../../../RegexConstants';
import { environment } from '../../../../environments/environment';
import { InspectionDetail, InspectionLineDetail, InspectionDetailsWithLine } from '../../../models/inpection-detail.model';
import { AemsFile } from '../../../models/aems-file.model';
import { forEach } from 'lodash-es';

@Component({
  selector: 'app-repair-line-item',
  imports: [ReactiveFormsModule, FileUploadComponent, CreateWorkOrderRepairPartsComponent, NgxSkeletonLoaderModule],
  templateUrl: './repair-line-item.component.html',
  styleUrl: './repair-line-item.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RepairLineItemComponent implements OnInit {
  @Input({ required: true }) repairLineItem!: FormGroup<RepairLineItemForm>;
  @Input() existingRepairLineItem: MaintenanceOrderLineItem | undefined;
  mode = input.required<Mode>();
  inspectionDetails = input.required<InspectionDetail[]>();
  isEditMode = input.required<boolean>();
  @Output() inspectionDetailsAfterUserSelectionDoneEvent = new EventEmitter<InspectionDetail[]>();
  shop = input.required<Shop>();
  controlId = input.required<string>();
  equipment = input.required<Equipment>();
  containerGrades = input.required<ContainerGrade[]>();
  workOrderCurrency = input.required<Currency | undefined>();
  damages = input.required<CodeDescription[]>();
  repairs = input.required<Repair[]>();
  repairLocations = input.required<RepairLocation[]>();
  tpiCodes = input.required<ThirdPartyIndicator[]>();
  @Input({ required: true }) index!: number;
  @Output() repairLineItemRemoved = new EventEmitter<number>();
  parts: Part[] = [];
  loadingParts = signal<boolean>(false);
  repairLineItemAmountWithoutParts: number = 0;
  partsTotalAmount: number = 0;
  isPartsMandatory: boolean = false;
  showOptionalParts: boolean = false;
  partsSnapshotData: MaintenanceOrderPart[] | undefined;
  previousLineItemKey: string | null = null;
  resetTriggerValue: number = 0;
  // Repair code the line item values were last set up for (prefilled on edit, or last applied on blur)
  private appliedRepairCode: string | null = null;
  @Output() lineItemKeyChanged = new EventEmitter<string>();

  // Controls data
  damageCodesTypeahead: IMcTypeaheadData[] = [];
  repairCodesTypeahead: IMcTypeaheadData[] = [];
  repairLocationCodesTypeahead: IMcTypeaheadData[] = [];
  tpiCodesTypeahead: IMcTypeaheadData[] = [];

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  constructor(
    private masterDataService: MasterDataService,
    private toastrService: ToastrService,
    private appTextSvc: ApplicationtTextService,
    private sharedDataService: SharedDataService,
  ) {}
  ngOnInit(): void {
    this.damageCodesTypeahead = this.mapCodeDescriptionToTypeaheadData(this.damages());
    this.repairCodesTypeahead = this.mapCodeDescriptionToTypeaheadData(this.repairs());
    this.repairLocationCodesTypeahead = this.mapCodeDescriptionToTypeaheadData(this.repairLocations());
    this.tpiCodesTypeahead = this.mapTPICodeToTypeaheadData(this.tpiCodes());
    this.calculateRepairItemTotalCost();
    this.addValidatorsToDamageCode();
    this.addValidatorsToRepairCode();
    this.addValidatorsToRepairLocationCode();
    this.addValidatorsToTpiCode();
    this.resetEirInspectionImageSelectionOnLineKeyChange();
    if (this.existingRepairLineItem) {
      this.partsSnapshotData = this.existingRepairLineItem.maintenanceOrderParts;
      this.fillForm(this.existingRepairLineItem);
    }
  }
  ngOnDestroy(): void {
    //this.removeWarnings();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  isControlInErrorState(control: AbstractControl | null): boolean | null {
    if (control?.invalid && control.touched) {
      return true;
    }
    return null;
  }
  isDamageAndRepairCodeValid(): boolean | undefined {
    return this.repairLineItem.controls.damageCode.valid && this.repairLineItem.controls.repairCode.valid;
  }

  addOptionalParts() {
    this.showOptionalParts = true;
  }

  allPartsRemoved() {
    this.showOptionalParts = false;
    if (this.existingRepairLineItem?.maintenanceOrderParts) {
      this.existingRepairLineItem.maintenanceOrderParts = [];
    }
  }

  repairHasExistingParts(): boolean {
    return this.existingRepairLineItem &&
      this.existingRepairLineItem.maintenanceOrderParts &&
      this.existingRepairLineItem.maintenanceOrderParts.length > 0
      ? true
      : false;
  }

  /**
   * @description Event handler when the repair code is selected from the typeahead control
   * @param event selected element from the typeahead control in template
   * Does nothing when the repair code has not changed, so focusing in and out of the field (e.g. tabbing past it on edit)
   * keeps the entered pieces, costs and parts
   * Resets the part array and parts form array control if the selected repair code does not have any parts
   * Add validators and set value of Man Hr/Pc, pieces , and mat cost/pc control of a repair line item
   * Checks if the selected repair code has any part associated with it, if yes, fetches the parts
   */
  onRepairCodeOptionSelected(event: FocusEvent) {
    const repairCode = (event.target as HTMLInputElement).value;
    if (repairCode === this.appliedRepairCode) {
      return;
    }
    this.appliedRepairCode = repairCode;
    this.parts = [];
    this.repairLineItem.controls.parts.clear();
    this.allPartsRemoved();
    let selectedRepair = this.repairs().find((repair) => repair.code === repairCode);
    if (selectedRepair) {
      this.addRequiredAndMaxValidators(this.repairLineItem, selectedRepair);
      this.repairLineItem.controls.manHoursPerPiece.setValue(utils.formatNumberToTwoDigitDecimal(selectedRepair.maxLabourHours));
      this.repairLineItem.controls.pieces.setValue(1);
      this.repairLineItem.controls.materialCostPerPiece.setValue(0);
      if (selectedRepair.allowParts === true) {
        this.isPartsMandatory = selectedRepair.isPartsMandatory;
        this.getParts(selectedRepair);
      }
      this.repairLineItem.markAllAsTouched();
      // Feature flag for PQC check
      if (environment.Features.EnablePQCValidation) {
        if (this.isRepairCodePQCUpgrade(repairCode) && this.mode().code === '03') {
          this.masterDataService
            .getShopInventoryAndForecastForEquipmentTypeAndGrade(this.shop().maintenanceShopCode, this.equipment().equipmentSize)
            .subscribe((response) => {
              if (response) {
                if (response.bookingForecastCount < response.totalEquipmentCount) {
                  this.toastrService.showToastr(this.appTextSvc.pqcWarningMessage, 'warning', { duration: 10000 });
                }
              }
            });
        }
      }
    }
  }

  private isRepairCodePQCUpgrade(repairCode: string): boolean {
    const pqcUpgradeCodes = ['0910', '0911', '0922', '0923', '0924', '0925'];
    return pqcUpgradeCodes.includes(repairCode);
  }
  private onRepairCodePrefilledDuringEdit(repair: Repair) {
    this.parts = [];
    this.addRequiredAndMaxValidators(this.repairLineItem, repair);
    if (repair.allowParts === true) {
      this.isPartsMandatory = repair.isPartsMandatory;
      this.getParts(repair);
    }
  }

  /**
   * The repair parts list only holds active parts, so on edit a part saved on this line item that has since been
   * deactivated would be missing, and the saved part could not be shown, validated or saved.
   * While the line item keeps its saved repair code, adds those saved parts back with their saved price,
   * capped at the saved quantity so no more of an inactive part can be added.
   */
  private withSavedParts(repairCode: string, parts: Part[]): Part[] {
    if (!this.partsSnapshotData || repairCode !== this.existingRepairLineItem?.maintenanceTypeCode) {
      return parts;
    }
    const missingSavedParts = this.partsSnapshotData
      .filter((savedPart) => !parts.some((part) => part.partCode === savedPart.partNumber))
      .map(
        (savedPart): Part => ({
          code: savedPart.partNumber,
          description: savedPart.partName ?? '',
          partCode: savedPart.partNumber,
          partDescription: savedPart.partName ?? '',
          repairCode,
          repairMode: this.mode().code,
          shopCode: this.shop().maintenanceShopCode,
          partManufacturerCode: '',
          shopPartManufacturerDiscount: '',
          shopImportTax: 0,
          basePricePerUnit: savedPart.unitPrice,
          finalPriceAfterDiscountAndMarkupPerUnit: savedPart.unitPrice,
          maxQuantity: savedPart.unitQuantity,
        }),
      );
    return [...parts, ...missingSavedParts];
  }

  onRepairPartsTotalChanged(event: number) {
    this.partsTotalAmount = event;
    this.repairLineItem.controls.itemTotalCost.setValue(
      utils.numberFormatter.format(this.repairLineItemAmountWithoutParts + this.partsTotalAmount),
    );
  }

  onRemoveRepairLineItemClicked() {
    this.repairLineItemRemoved.emit(this.index);
  }

  private addRequiredAndMaxValidators(repairLineItem: FormGroup<RepairLineItemForm>, selectedRepair: Repair) {
    let piecesControl = repairLineItem.controls.pieces;
    let materialCostPerPieceControl = repairLineItem.controls.materialCostPerPiece;
    let manhoursPerPieceControl = repairLineItem.controls.manHoursPerPiece;

    manhoursPerPieceControl.clearValidators();
    manhoursPerPieceControl.addValidators([
      Validators.required,
      Validators.pattern(RegexConstants.DIGIT_UPTO_TWO_DECIMAL),
      Validators.max(utils.formatNumberToTwoDigitDecimal(selectedRepair.maxLabourHours)),
    ]);
    manhoursPerPieceControl.updateValueAndValidity();

    piecesControl.clearValidators();
    piecesControl.addValidators([
      Validators.required,
      Validators.pattern(RegexConstants.DIGIT_WITH_NO_DECIMAL),
      Validators.max(selectedRepair.maxQuantity),
    ]);
    piecesControl.updateValueAndValidity();

    materialCostPerPieceControl.clearValidators();
    materialCostPerPieceControl.addValidators([
      Validators.required,
      Validators.pattern(RegexConstants.DIGIT_UPTO_TWO_DECIMAL),
      Validators.max(utils.formatNumberToTwoDigitDecimal(selectedRepair.maxMaterialCost)),
    ]);
    materialCostPerPieceControl.updateValueAndValidity();
  }
  /**
   *
   * @param repairLineItem Private methods regions
   */
  private calculateRepairItemTotalCost() {
    combineLatest([this.repairLineItem.controls.pieces.valueChanges, this.repairLineItem.controls.materialCostPerPiece.valueChanges])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([pieces, materialCostPerPiece]) => {
        this.repairLineItemAmountWithoutParts = pieces * materialCostPerPiece;
        this.repairLineItem.controls.itemTotalCost.setValue(
          utils.numberFormatter.format(this.repairLineItemAmountWithoutParts + this.partsTotalAmount),
        );
      });
  }

  private addValidatorsToDamageCode() {
    let damageCode = this.repairLineItem.controls.damageCode;
    damageCode.clearValidators();
    damageCode.addValidators([Validators.required, typeaheadCodeExistValidator(this.damageCodesTypeahead)]);
    damageCode.updateValueAndValidity();
  }

  /**
   *
   * @param repairLineItem the line item to which this repair code belongs to
   * @description Add validators to the repair code control
   */
  private addValidatorsToRepairCode() {
    let repairCodeControl = this.repairLineItem.controls.repairCode;
    repairCodeControl.clearValidators();
    repairCodeControl.addValidators([
      Validators.required,
      typeaheadCodeExistValidator(this.repairCodesTypeahead),
      containerGradeValidator(this.equipment(), this.containerGrades(), this.mode()),
      repairSuspendedValidator(this.repairs()),
      ptiStatusValidator(this.equipment(), this.mode(), this.toastrService, this.appTextSvc),
    ]);

    if (this.equipment()?.isLastMoveGateInEmpty) {
      this.sharedDataService.getPastRepairs().subscribe((pastRepairs) => {
        if (pastRepairs.length > 0) {
          repairCodeControl.addValidators(duplicateRepairCodeValidator(pastRepairs, this.toastrService, this.appTextSvc));
          repairCodeControl.updateValueAndValidity();
        }
      });
    }

    if (!this.equipment()?.isContainerEmpty) {
      repairCodeControl.addValidators(
        nonEmptyReeferRepairWarning(this.index, this.equipment(), this.repairs(), this.toastrService, this.appTextSvc),
      );
    }
    repairCodeControl.updateValueAndValidity();
  }

  /**
   *
   * @param repairLineItem the line item to which this repair location code belongs to
   * @description Add validators to the repair location code control
   */
  private addValidatorsToRepairLocationCode() {
    let repairLocationCode = this.repairLineItem.controls.repairLocationCode;
    repairLocationCode.clearValidators();
    repairLocationCode.addValidators([Validators.required, typeaheadCodeExistValidator(this.repairLocationCodesTypeahead)]);
    repairLocationCode.updateValueAndValidity();
  }

  /**
   *
   * @param repairLineItem the line item to which this tpi code belongs to
   * @description Add validators to the tpi code control
   */
  private addValidatorsToTpiCode() {
    let tpiCode = this.repairLineItem.controls.tpiCode;
    tpiCode.clearValidators();
    tpiCode.addValidators([Validators.required, typeaheadCodeExistValidator(this.tpiCodesTypeahead)]);
    tpiCode.updateValueAndValidity();
  }

  private getParts(repairCode: Repair) {
    this.loadingParts.set(true);
    this.masterDataService
      .getRepairParts(
        this.mode().code,
        repairCode.code,
        this.shop().maintenanceShopCode,
        this.workOrderCurrency()?.exchangeRateFromUsd ?? 0,
      )
      .pipe(takeUntil(this.destroyed$))
      .subscribe((parts) => {
        this.parts = this.withSavedParts(repairCode.code, parts);
        this.loadingParts.set(false);
      });
  }

  /*private removeWarnings() {
      this.warningSharedService.deleteWarning("duplicateRepairCodeValidator" + this.index);
      this.warningSharedService.deleteWarning("ptiStatusValidator" + this.index);
      this.warningSharedService.deleteWarning("exceptionalReapirValiator" + this.index);
   }*/

  private mapCodeDescriptionToTypeaheadData(data: CodeDescription[]): IMcTypeaheadData[] {
    return data.map((codeDescription) => {
      return {
        label: codeDescription.code,
        sublabel: codeDescription.description,
        value: codeDescription.code,
      };
    });
  }

  private mapTPICodeToTypeaheadData(data: ThirdPartyIndicator[]): IMcTypeaheadData[] {
    return data.map((codeDescription) => {
      return {
        label: getTPIChar(codeDescription.code),
        sublabel: codeDescription.displayText,
        value: codeDescription.code,
      };
    });
  }

  private fillForm(item: MaintenanceOrderLineItem) {
    this.appliedRepairCode = item.maintenanceTypeCode;
    let repair = this.repairs().filter((repair) => repair.code === item.maintenanceTypeCode)[0];
    if (repair) {
      this.onRepairCodePrefilledDuringEdit(repair);
      this.repairLineItem.patchValue({
        damageCode: item.equipmentDamageCode,
        repairCode: item.maintenanceTypeCode,
        repairLocationCode: item.maintenanceRepairLocationCode,
        tpiCode: getTPIChar(item.thirdPartyIndicator),
        pieces: item.repairQuantity,
        materialCostPerPiece: item.materialCostPerPiece,
        manHoursPerPiece: item.manHoursPerPiece,
        itemTotalCost: utils.numberFormatter.format(
          item.repairQuantity * item.materialCostPerPiece +
            item.maintenanceOrderParts.reduce(
              (accumulator, currentPart) => accumulator + currentPart.unitPrice * currentPart.unitQuantity,
              0,
            ),
        ),
      });
    }
    this.repairLineItem.markAllAsTouched();
  }

  get inspectionDetailsForUpload(): InspectionDetailsWithLine {
    const lineDetail = this.getCurrentLineDetail();
    const lineItemKey = `${lineDetail.repairCode}-${lineDetail.damageCode}-${lineDetail.repairLocation}`;

    const filteredInspectionDetails = this.inspectionDetails().map((detail) => ({
      ...detail,
      damageImages:
        detail.damageImages?.filter(
          (image) =>
            image.linkedLineItemIdentifier === '' ||
            image.linkedLineItemIdentifier === null ||
            image.linkedLineItemIdentifier === undefined ||
            image.linkedLineItemIdentifier === lineItemKey,
        ) || [],
    }));

    return {
      inspectionDetails: filteredInspectionDetails,
      lineDetail: lineDetail,
      attachReport: false,
      isEditMode: this.isEditMode(),
    };
  }

  private getCurrentLineDetail(): InspectionLineDetail {
    return {
      damageCode: this.repairLineItem.controls.damageCode.value,
      repairCode: this.repairLineItem.controls.repairCode.value,
      repairLocation: this.repairLineItem.controls.repairLocationCode.value,
      repairDescription: this.repairs().find((repair) => repair.code === this.repairLineItem.controls.repairCode.value)?.description ?? '',
    };
  }

  onInspectionDetailsWithLineAfterUserSelectionDone(event: InspectionDetailsWithLine) {
    this.inspectionDetailsAfterUserSelectionDoneEvent.emit(event.inspectionDetails);
  }

  get lineAttachment(): AemsFile[] {
    let filteredFiles = [...(this.existingRepairLineItem?.blobResolvedFiles ?? [])];

    if (this.isEditMode() && this.inspectionDetails().length > 0) {
      forEach(this.inspectionDetails(), (detail) => {
        forEach(detail.damageImages, (image) => {
          let generatedName = image.guid ?? '';
          filteredFiles = filteredFiles.filter((f) => f.generatedName !== generatedName);
        });
      });

      return filteredFiles;
    }
    return filteredFiles;
  }

  private resetEirInspectionImageSelectionOnLineKeyChange(): void {
    combineLatest([
      this.repairLineItem.controls.damageCode.valueChanges.pipe(startWith(this.repairLineItem.controls.damageCode.value)),
      this.repairLineItem.controls.repairCode.valueChanges.pipe(startWith(this.repairLineItem.controls.repairCode.value)),
      this.repairLineItem.controls.repairLocationCode.valueChanges.pipe(startWith(this.repairLineItem.controls.repairLocationCode.value)),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([damageCode, repairCode, repairLocationCode]) => {
        const currentLineItemKey = `${repairCode || ''}-${damageCode || ''}-${repairLocationCode || ''}`;
        if (this.previousLineItemKey !== null && this.previousLineItemKey !== currentLineItemKey) {
          let resetLineItemKey: string = this.previousLineItemKey ?? '';
          if (resetLineItemKey) {
            this.lineItemKeyChanged.emit(resetLineItemKey);
            this.resetTriggerValue = Date.now();
          }
        }
        this.previousLineItemKey = currentLineItemKey;
      });
  }
}
